import { queryOptions, useQueryClient, useSuspenseQuery, type QueryClient } from '@tanstack/react-query';
import type { ObjectsListResponse, operations } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { objectKeys, objectLookupKey, parseObject, type ObjectListParams, type ObjectRecord } from './entity';

export type ObjectList = Omit<ObjectsListResponse, 'objects'> & { objects: ObjectRecord[] };

export async function getObjects(workspaceId: number, params: ObjectListParams, qc: QueryClient): Promise<ObjectList> {
  const { objectTypeId, ...query } = params;
  const response = await hydraUiApi.objects.getObjectTypesObjects({
    params: { path: { environment_id: String(workspaceId), type_id: objectTypeId }, query },
  });
  const objects = response.objects.map(parseObject);
  for (const record of objects) qc.setQueryData(objectKeys.detail(workspaceId, record.id), record);
  return { ...response, objects };
}

export const getObjectsQueryOptions = (workspaceId: number, params: ObjectListParams, qc: QueryClient) =>
  queryOptions({
    queryKey: objectKeys.list(workspaceId, params),
    queryFn: () => getObjects(workspaceId, params, qc),
    staleTime: 0,
  });

type UseObjectsOptions = {
  params: ObjectListParams;
  queryConfig?: QueryConfig<typeof getObjectsQueryOptions>;
};

export function useObjects({ params, queryConfig }: UseObjectsOptions) {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({ ...getObjectsQueryOptions(workspaceId, params, qc), ...queryConfig });
}

export async function getObject(workspaceId: number, objectTypeId: string, identifier: string): Promise<ObjectRecord> {
  const response = await hydraUiApi.objects.getObjectTypesObjectsByIdentifier({
    params: { path: { environment_id: String(workspaceId), type_id: objectTypeId, identifier } },
  });
  return parseObject(response);
}

export const getObjectQueryOptions = (workspaceId: number, objectTypeId: string, identifier: string, qc: QueryClient) =>
  queryOptions({
    queryKey: objectLookupKey(workspaceId, objectTypeId, identifier),
    queryFn: async () => {
      const record = await getObject(workspaceId, objectTypeId, identifier);
      qc.setQueryData(objectKeys.detail(workspaceId, record.id), record);
      return record;
    },
    staleTime: 0,
  });

type UseObjectOptions = {
  objectTypeId: string;
  identifier: string;
  queryConfig?: QueryConfig<typeof getObjectQueryOptions>;
};

export function useObject({ objectTypeId, identifier, queryConfig }: UseObjectOptions) {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({ ...getObjectQueryOptions(workspaceId, objectTypeId, identifier, qc), ...queryConfig });
}

type LogQuery = NonNullable<operations['get-v1-environments-environment_id-logs']['parameters']['query']>;
export type ObjectLogsParams = Pick<LogQuery, 'type' | 'name' | 'limit' | 'filters'>;

export function queryObjectLogs(workspaceId: number, cioObjectId: string, params: ObjectLogsParams = {}) {
  return hydraUiApi.logs.list({
    params: {
      path: { environment_id: String(workspaceId) },
      query: { ...params, cio_object_id: cioObjectId, match_limit: params.filters == null ? undefined : params.limit },
    },
  });
}
